import { defineStore } from "pinia";
import { ref, computed } from "vue";
import AdminService from "../services/admin.service";
import type {
  Manager,
  Restaurant,
  Company,
  CreateManagerRequest,
  UpdateManagerRequest,
  CreateRestaurantRequest,
  UpdateRestaurantRequest,
  CreateCompanyRequest,
  UpdateCompanyRequest,
} from "../types";

export const useAdminStore = defineStore("admin", () => {
  // State
  const managers = ref<Manager[]>([]);
  const restaurants = ref<Restaurant[]>([]);
  const companies = ref<Company[]>([]);
  const selectedRestaurant = ref<Restaurant | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const activeManagers = computed(() =>
    managers.value.filter((m) => m.isActive)
  );

  const activeRestaurants = computed(() =>
    restaurants.value.filter((r) => r.isActive)
  );

  // Manager Actions
  async function fetchManagers() {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await AdminService.getManagers();
      const data = response.data;
      // API response: { managers: [...], count: N } veya array
      if (Array.isArray(data)) {
        managers.value = data;
      } else if (data?.managers) {
        managers.value = data.managers;
      } else if (data?.Managers) {
        managers.value = data.Managers;
      } else {
        managers.value = [];
      }
      return managers.value;
    } catch (err: any) {
      error.value = err.response?.data?.message || "Failed to fetch managers";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function createManager(data: CreateManagerRequest) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await AdminService.createManager(data);
      managers.value.push(response.data);
      return response.data;
    } catch (err: any) {
      error.value = err.response?.data?.message || "Failed to create manager";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function updateManager(data: UpdateManagerRequest) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await AdminService.updateManager(data);
      const index = managers.value.findIndex((m) => m.id === data.id);
      if (index !== -1) {
        managers.value[index] = response.data;
      }
      return response.data;
    } catch (err: any) {
      error.value = err.response?.data?.message || "Failed to update manager";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function deleteManager(id: string) {
    isLoading.value = true;
    error.value = null;

    try {
      await AdminService.deleteManager(id);
      managers.value = managers.value.filter((m) => m.id !== id);
    } catch (err: any) {
      error.value = err.response?.data?.message || "Failed to delete manager";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  // Restaurant Actions
  async function fetchRestaurants() {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await AdminService.getRestaurants();
      const data = response.data;
      // API response: { restaurants: [...], count: N } veya array
      if (Array.isArray(data)) {
        restaurants.value = data;
      } else if (data?.restaurants) {
        restaurants.value = data.restaurants;
      } else if (data?.Restaurants) {
        restaurants.value = data.Restaurants;
      } else {
        restaurants.value = [];
      }
      return restaurants.value;
    } catch (err: any) {
      error.value =
        err.response?.data?.message || "Failed to fetch restaurants";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function createRestaurant(data: CreateRestaurantRequest) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await AdminService.createRestaurant(data);
      restaurants.value.push(response.data);
      return response.data;
    } catch (err: any) {
      error.value =
        err.response?.data?.message || "Failed to create restaurant";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function updateRestaurant(data: UpdateRestaurantRequest) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await AdminService.updateRestaurant(data);
      const index = restaurants.value.findIndex((r) => r.id === data.id);
      if (index !== -1) {
        restaurants.value[index] = response.data;
      }
      return response.data;
    } catch (err: any) {
      error.value =
        err.response?.data?.message || "Failed to update restaurant";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function deleteRestaurant(id: string) {
    isLoading.value = true;
    error.value = null;

    try {
      await AdminService.deleteRestaurant(id);
      restaurants.value = restaurants.value.filter((r) => r.id !== id);
    } catch (err: any) {
      error.value =
        err.response?.data?.message || "Failed to delete restaurant";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function selectRestaurant(restaurantId: string) {
    try {
      const response = await AdminService.selectRestaurant(restaurantId);
      selectedRestaurant.value = response.data.restaurant;
      return response.data;
    } catch (err: any) {
      error.value =
        err.response?.data?.message || "Failed to select restaurant";
      throw err;
    }
  }

  // Company Actions
  async function fetchCompanies() {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await AdminService.getCompanies();
      const data = response.data;
      // API response: { companies: [...], count: N } veya array
      if (Array.isArray(data)) {
        companies.value = data;
      } else if (data?.companies) {
        companies.value = data.companies;
      } else if (data?.Companies) {
        companies.value = data.Companies;
      } else {
        companies.value = [];
      }
      return companies.value;
    } catch (err: any) {
      error.value = err.response?.data?.message || "Failed to fetch companies";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function createCompany(data: CreateCompanyRequest) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await AdminService.createCompany(data);
      companies.value.push(response.data);
      return response.data;
    } catch (err: any) {
      error.value = err.response?.data?.message || "Failed to create company";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function updateCompany(data: UpdateCompanyRequest) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await AdminService.updateCompany(data);
      const index = companies.value.findIndex((c) => c.id === data.id);
      if (index !== -1) {
        companies.value[index] = response.data;
      }
      return response.data;
    } catch (err: any) {
      error.value = err.response?.data?.message || "Failed to update company";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function deleteCompany(id: string) {
    isLoading.value = true;
    error.value = null;

    try {
      await AdminService.deleteCompany(id);
      companies.value = companies.value.filter((c) => c.id !== id);
    } catch (err: any) {
      error.value = err.response?.data?.message || "Failed to delete company";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  // Test Data
  async function createTestData() {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await AdminService.createMultiRestaurantTestData();
      await Promise.all([
        fetchCompanies(),
        fetchRestaurants(),
        fetchManagers(),
      ]);
      return response.data;
    } catch (err: any) {
      error.value = err.response?.data?.message || "Failed to create test data";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  return {
    // State
    managers,
    restaurants,
    companies,
    selectedRestaurant,
    isLoading,
    error,
    // Getters
    activeManagers,
    activeRestaurants,
    // Actions
    fetchManagers,
    createManager,
    updateManager,
    deleteManager,
    fetchRestaurants,
    createRestaurant,
    updateRestaurant,
    deleteRestaurant,
    selectRestaurant,
    fetchCompanies,
    createCompany,
    updateCompany,
    deleteCompany,
    createTestData,
  };
});
